import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation, useParams, useNavigate } from "react-router-dom";
import { isNativePlatform } from "@/lib/platform";

// Router selection: BrowserRouter on web (default), HashRouter on native.
// Capacitor native WebViews load from a file:// origin where path-based routes
// do not survive reload. HashRouter uses `#/path` which works in both. On web
// isNativePlatform() is always false, so BrowserRouter is preserved exactly.
const Router = isNativePlatform() ? HashRouter : BrowserRouter;
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ChatProvider, useChatContext } from "@/contexts/ChatContext";
import { GuideProvider } from "@/contexts/GuideContext";
import { LRDirectGateProvider } from "@/contexts/LRDirectGateContext";
import { ProductProvider } from "@/contexts/ProductContext";
import { LRDirectRouteGuard } from "@/components/lr-direct/LRDirectRouteGuard";
import { LRDirectPaywallModal } from "@/components/lr-direct/LRDirectPaywallModal";
import { ThemeProvider } from "next-themes";

import { AppShellLayout } from "@/components/layout/AppShellLayout";
import { RoleGate } from "@/components/auth/RoleGate";
import MessengerDock from "@/components/chat/MessengerDock";
import { AtlasFloatingDock } from "@/components/atlas/AtlasFloatingDock";

import ReferralCapture from "@/components/ReferralCapture";
import { ScrollToTop } from "@/components/ScrollToTop";
import AdminAffiliates from "./pages/AdminAffiliates";
import AdminVerification from "./pages/AdminVerification";
import AdminHubModeration from "./pages/AdminHubModeration";

import AffiliatesPage from "./pages/AffiliatesPage";
import PartnerProgramPage from "./pages/PartnerProgramPage";
import Index from "./pages/Index";
import LoadBoardLandingPage from "./pages/LoadBoardLandingPage";
import BrokerDashboard from "./pages/BrokerDashboardV2";
import CarrierDashboard from "./pages/CarrierDashboardV2";
import ShipperDashboard from "./pages/ShipperDashboardV2";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";
import ComingSoon from "./pages/ComingSoon";
import OptInPage from "./pages/OptInPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import CancellationPolicy from "./pages/CancellationPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import SupportPage from "./pages/SupportPage";
import CookieConsentBanner from "./components/cookies/CookieConsentBanner";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ProfileSettings from "./pages/ProfileSettings";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminSignups from "./pages/AdminSignups";
import AdminCompanies from "./pages/AdminCompanies";
import AdminFMCSARequestsNew from "./pages/AdminFMCSARequestsNew";
import ShippersPage from "./pages/ShippersPage";
import BrokersPage from "./pages/BrokersPage";
import CarriersPage from "./pages/CarriersPage";
import PricingPage from "./pages/PricingPage";
import LRDirectAccessPage from "./pages/LRDirectAccessPage";
// import AboutPage from "./pages/AboutPage"; // Temporarily disabled
import AdminCitiesImport from "./pages/AdminCitiesImport";
import SearchLoadsPageV2 from "./pages/SearchLoadsPageV2";
import SearchTrucksPageV2 from "./pages/SearchTrucksPageV2";
import PostLoadPageV2 from "./pages/PostLoadPageV2";
import PostTruckPageV2 from "./pages/PostTruckPageV2";
import AIPostPage from "./pages/AIPostPage";
import AIPostPageCarrier from "./pages/AIPostPageCarrier";
import MyLoadsPageV2 from "./pages/MyLoadsPageV2";
import MyTrucksPageV2 from "./pages/MyTrucksPageV2";
import MarketplacePageV2 from "./pages/MarketplacePageV2";
import SocialHubPage from "./pages/SocialHubPage";
import AtlasFreightLabPage from "./pages/AtlasFreightLabPage";
import LRDirectPostPage from "./pages/LRDirectPostPage";
import LRDirectGuidedWizardPage from "./pages/LRDirectGuidedWizardPage";
import LRDirectAtlasPage from "./pages/LRDirectAtlasPage";
import LRDirectPage from "./pages/LRDirectPage";
import LRDirectBoardPage from "./pages/LRDirectBoardPage";
import LRDirectMyOpportunitiesPage from "./pages/LRDirectMyOpportunitiesPage";
import LRDirectMyWinsPage from "./pages/LRDirectMyWinsPage";
import BrokerLRDirectDashboardV2 from "./pages/BrokerLRDirectDashboardV2";
import LRDirectOpportunityPage from "./pages/LRDirectOpportunityPage";
import LRDirectLoadDetailPage from "./pages/LRDirectLoadDetailPage";
import LRDirectDispatchSheetPage from "./pages/LRDirectDispatchSheetPage";

import LRDirectDispatchBuilderPage from "./pages/LRDirectDispatchBuilderPage";
import LRDirectAddressBookPage from "./pages/LRDirectAddressBookPage";

import LRDirectNetworkSettingsPage from "./pages/LRDirectNetworkSettingsPage";
import LRDirectNetworkInvitationsPage from "./pages/LRDirectNetworkInvitationsPage";

import LRDirectShippersPage from "./pages/LRDirectShippersPage";
import LRDirectBrokerMyNetworkPage from "./pages/LRDirectBrokerMyNetworkPage";
import LRDirectMyQuotesPage from "./pages/LRDirectMyQuotesPage";
import LRDirectMyQuoteDetailPage from "./pages/LRDirectMyQuoteDetailPage";
import LRDirectBrokerQuotesPage from "./pages/LRDirectBrokerQuotesPage";
import LiveLoadRoomPage from "./pages/LiveLoadRoomPage";
import CompanySettingsPage from "./pages/CompanySettingsPage";
import BillingPage from "./pages/BillingPage";
import InvestorsPage from "./pages/InvestorsPage";
import ClockHelpPage from "./pages/ClockHelpPage";
import JoinCompanyPage from "./pages/JoinCompanyPage";
import ReactivatePage from "./pages/ReactivatePage";
import BillingReturnPage from "./pages/BillingReturnPage";
import SafetyReportPage from "./pages/SafetyReportPage";
import LoginAlias from "./pages/LoginAlias";
import SignupAlias from "./pages/SignupAlias";
import DispatcherPage from "./pages/DispatcherPage";
// Prefetch is now deferred to PlaceAutocomplete first focus - see place-autocomplete.tsx

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// NativeGate: On Capacitor native, marketing/public routes ('/', '/shippers',
// '/brokers', '/carriers', '/pricing', '/lr-direct-access', '/investors',
// '/affiliates', '/about', '/coming-soon') redirect to '/auth'. The native
// app is login-only; marketing content lives on lanerunner.com. On web,
// isNativePlatform() is always false so this is a no-op.
const NATIVE_MARKETING_ROUTES = new Set([
  '/', '/shippers', '/brokers', '/carriers', '/pricing', '/load-board',
  '/lr-direct-access', '/investors', '/affiliates', '/about',
  '/coming-soon',
]);

const NativeGate = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (!isNativePlatform()) return <>{children}</>;
  if (!NATIVE_MARKETING_ROUTES.has(location.pathname)) return <>{children}</>;
  // Wait for session restore before deciding — prevents login flash on cold boot
  if (loading) return null;
  // Signed-in user on '/' → let <Index> render and route to their dashboard
  if (user && location.pathname === '/') return <>{children}</>;
  // Signed-out (or on another marketing route) → login
  return <Navigate to="/auth?mode=login" replace />;
};


// Redirect helper — old /lr-direct/quote-requests/:id → /lr-direct/my-quotes/:id
const LegacyQuoteDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/lr-direct/my-quotes/${id ?? ""}`} replace />;
};

// App-level protection component
const AppRoutes = () => {
  const { user, loading, hasActiveSubscription, isDemoUser, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { openConversationFromNotification } = useChatContext();

  // Register/unregister native push notifications when auth state changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { registerPushForUser, unregisterPushForUser } = await import("@/lib/push/registerPush");
      if (cancelled) return;
      if (user?.id) {
        await registerPushForUser(user.id, (link) => {
          try {
            // DM push links look like `/messages?c=<conversation_id>`. There is
            // no /messages route — the messenger is a floating dock — so open
            // the dock for that conversation instead of navigating.
            const dm = link.match(/^\/messages\?c=([^&#]+)/);
            if (dm) {
              openConversationFromNotification(decodeURIComponent(dm[1]));
              return;
            }
            navigate(link);
          } catch (e) { console.warn("push nav failed", e); }
        });
      } else {
        await unregisterPushForUser("");
      }
    })().catch((e) => console.warn("push setup failed", e));
    return () => { cancelled = true; };
  }, [user?.id, navigate, openConversationFromNotification]);

  if (loading) {
    return (
      <div className="min-h-screen bg-lr-dark flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Protected routes that require authentication
  const protectedRoutes = ['/broker-dashboard', '/carrier-dashboard', '/shipper-dashboard', '/shipper-dashboard-lanerunner', '/profile-settings', '/company-settings', '/admin', '/search', '/search-trucks', '/post-load', '/post', '/post-truck', '/atlas-hub', '/atlas-hub-carrier', '/my-loads', '/my-trucks', '/marketplace', '/social', '/atlas-lab', '/report-fraud', '/dispatcher'];
  const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route)) || location.pathname === '/lr-direct' || location.pathname.startsWith('/lr-direct/');

  if (isProtectedRoute && !user) {
    const nextPath = location.pathname + location.search;
    return <Navigate to={`/auth?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  // Subscription gate — block product routes for inactive subscriptions
  // Exempt paths: billing management, profile, auth flows, admin, public pages
  const subscriptionExemptPaths = [
    '/auth', '/reactivate', '/billing/return', '/company-settings', '/profile-settings',
    '/join', '/admin', '/forgot-password', '/reset-password',
    '/privacy', '/terms', '/cancellation-policy', '/cookies', '/investors', '/clock-help',
    '/shippers', '/brokers', '/carriers', '/pricing', '/lr-direct-access',
    '/coming-soon', '/opt-in', '/report-fraud', '/support'
  ];
  const isSubscriptionExempt =
    subscriptionExemptPaths.some(p => location.pathname.startsWith(p)) ||
    location.pathname === '/';

  if (user && isProtectedRoute && !isSubscriptionExempt && !hasActiveSubscription && !isDemoUser && !isAdmin) {
    return <Navigate to="/reactivate" replace />;
  }


  return (
    <>
      <ReferralCapture />
      <NativeGate>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/load-board" element={<LoadBoardLandingPage />} />
        <Route path="/shippers" element={<ShippersPage />} />
        <Route path="/brokers" element={<BrokersPage />} />
        <Route path="/carriers" element={<CarriersPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/lr-direct-access" element={<LRDirectAccessPage />} />
        {/* <Route path="/about" element={<AboutPage />} /> */}{/* Temporarily disabled */}
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/opt-in" element={<OptInPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/cancellation-policy" element={<CancellationPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/investors" element={<InvestorsPage />} />
        <Route path="/affiliates" element={<AffiliatesPage />} />
        <Route path="/partner-program" element={<PartnerProgramPage />} />
        <Route path="/clock-help" element={<ClockHelpPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/profile-settings" element={<ProfileSettings />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/companies" element={<AdminCompanies />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/signups" element={<AdminSignups />} />
        <Route path="/admin/fmcsa-requests" element={<AdminFMCSARequestsNew />} />
        <Route path="/admin/import-cities" element={<AdminCitiesImport />} />
        <Route path="/admin/affiliates" element={<AdminAffiliates />} />
        <Route path="/admin/verification" element={<AdminVerification />} />
        <Route path="/admin/hub-moderation" element={<AdminHubModeration />} />

        <Route path="/auth" element={<AuthPage />} />
        <Route path="/login" element={<LoginAlias />} />
        <Route path="/signin" element={<LoginAlias />} />
        <Route path="/signup" element={<SignupAlias />} />
        <Route path="/join" element={<JoinCompanyPage />} />
        <Route path="/reactivate" element={<ReactivatePage />} />
        <Route path="/billing/return" element={<BillingReturnPage />} />
        <Route path="/safety-report" element={<SafetyReportPage />} />
        <Route path="/report-fraud" element={<Navigate to="/safety-report" replace />} />

        {/* AppShell layout — persistent top bar, no remount on navigation */}
        <Route element={<AppShellLayout />}>
          <Route path="/broker-dashboard" element={<RoleGate allow={["broker","broker_carrier"]}><BrokerDashboard /></RoleGate>} />
          <Route path="/carrier-dashboard" element={<RoleGate allow={["carrier","broker_carrier"]}><CarrierDashboard /></RoleGate>} />
          <Route path="/shipper-dashboard" element={<RoleGate allow={["shipper"]}><ShipperDashboard /></RoleGate>} />
          <Route path="/shipper-dashboard-lanerunner" element={<RoleGate allow={["shipper"]}><BrokerDashboard /></RoleGate>} />
          <Route path="/search" element={<RoleGate allow={["broker","carrier","shipper","broker_carrier"]}><SearchLoadsPageV2 /></RoleGate>} />
          <Route path="/search-trucks" element={<RoleGate allow={["broker","carrier","shipper","broker_carrier"]}><SearchTrucksPageV2 /></RoleGate>} />
          <Route path="/post-load" element={<RoleGate allow={["broker","shipper","broker_carrier"]}><PostLoadPageV2 /></RoleGate>} />
          <Route path="/post" element={<RoleGate allow={["broker","shipper","broker_carrier"]}><PostLoadPageV2 /></RoleGate>} />
          <Route path="/post-truck" element={<RoleGate allow={["carrier","broker_carrier"]}><PostTruckPageV2 /></RoleGate>} />
          <Route path="/atlas-hub" element={<RoleGate allow={["broker","shipper","broker_carrier"]}><AIPostPage /></RoleGate>} />
          <Route path="/atlas-hub-carrier" element={<RoleGate allow={["carrier","broker_carrier"]}><AIPostPageCarrier /></RoleGate>} />
          <Route path="/my-loads" element={<RoleGate allow={["broker","shipper","broker_carrier"]}><MyLoadsPageV2 /></RoleGate>} />
          <Route path="/my-trucks" element={<RoleGate allow={["carrier","broker_carrier"]}><MyTrucksPageV2 /></RoleGate>} />
          <Route path="/marketplace" element={<RoleGate allow={["broker","carrier","shipper","broker_carrier"]}><MarketplacePageV2 /></RoleGate>} />
          <Route path="/social" element={<RoleGate allow={["broker","carrier","shipper","broker_carrier"]}><SocialHubPage /></RoleGate>} />
          <Route path="/atlas-lab" element={<RoleGate allow={["broker","carrier","shipper","broker_carrier"]}><AtlasFreightLabPage /></RoleGate>} />
          <Route path="/atlas-freight-lab" element={<RoleGate allow={["broker","carrier","shipper","broker_carrier"]}><AtlasFreightLabPage /></RoleGate>} />
          <Route path="/billing" element={<RoleGate allow={["broker","carrier","shipper","broker_carrier"]}><BillingPage /></RoleGate>} />
          <Route path="/company-settings" element={<RoleGate allow={["broker","carrier","shipper","broker_carrier"]}><CompanySettingsPage /></RoleGate>} />
          <Route path="/dispatcher" element={<RoleGate allow={["carrier","broker_carrier"]}><DispatcherPage /></RoleGate>} />
          <Route path="/lr-direct" element={<RoleGate allow={["broker","shipper","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/broker" element={<RoleGate allow={["broker","broker_carrier","carrier"]}><LRDirectRouteGuard><BrokerLRDirectDashboardV2 /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/board" element={<RoleGate allow={["broker","shipper","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectBoardPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/my-wins" element={<RoleGate allow={["broker","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectMyWinsPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/my-opportunities" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectMyOpportunitiesPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/my-loads" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectMyOpportunitiesPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/atlas" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectAtlasPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/post/start" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectGuidedWizardPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/post" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectPostPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/post/:id" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectPostPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/network-settings" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectNetworkSettingsPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/network-invitations" element={<RoleGate allow={["broker","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectNetworkInvitationsPage /></LRDirectRouteGuard></RoleGate>} />
          
          <Route path="/lr-direct/my-network" element={<RoleGate allow={["broker","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectBrokerMyNetworkPage /></LRDirectRouteGuard></RoleGate>} />
          {/* Legacy: old "Find Shippers" URL → new hub, which contains the lookup collapsed inside. */}
          <Route path="/lr-direct/shippers" element={<Navigate to="/lr-direct/my-network" replace />} />
          <Route path="/lr-direct/shippers-legacy" element={<RoleGate allow={["broker","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectShippersPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/opportunity/:id" element={<RoleGate allow={["broker","shipper","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectOpportunityPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/load/:id" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectLoadDetailPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/dispatches" element={<Navigate to="/lr-direct/my-loads?stage=dispatching" replace />} />
          <Route path="/lr-direct/dispatches/addresses" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectAddressBookPage /></LRDirectRouteGuard></RoleGate>} />

          <Route path="/lr-direct/dispatches/build/:loadId" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectDispatchBuilderPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/dispatches/won/:loadId" element={<RoleGate allow={["broker","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectDispatchBuilderPage readOnly /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/dispatch/:loadId" element={<RoleGate allow={["broker","shipper","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectDispatchSheetPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/live/:id" element={<RoleGate allow={["broker","shipper","broker_carrier","carrier"]}><LRDirectRouteGuard><LiveLoadRoomPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/my-quotes" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectMyQuotesPage /></LRDirectRouteGuard></RoleGate>} />
          <Route path="/lr-direct/my-quotes/:id" element={<RoleGate allow={["shipper"]}><LRDirectRouteGuard><LRDirectMyQuoteDetailPage /></LRDirectRouteGuard></RoleGate>} />
          {/* Legacy redirects — old Quote Requests routes → new My Quotes routes */}
          <Route path="/lr-direct/quote-requests" element={<Navigate to="/lr-direct/my-quotes" replace />} />
          <Route path="/lr-direct/quote-requests/new" element={<Navigate to="/lr-direct/post/start?mode=quote" replace />} />
          <Route path="/lr-direct/quote-requests/:id" element={<LegacyQuoteDetailRedirect />} />
          <Route path="/lr-direct/broker-quotes" element={<RoleGate allow={["broker","broker_carrier","carrier"]}><LRDirectRouteGuard><LRDirectBrokerQuotesPage /></LRDirectRouteGuard></RoleGate>} />
        </Route>

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </NativeGate>
      <MessengerDock />
      <AtlasFloatingDock />
      <LRDirectPaywallModal />
      <HomepageCookieBanner />
    </>
  );
};

const HomepageCookieBanner = () => {
  const { pathname } = useLocation();
  if (pathname !== '/') return null;
  return <CookieConsentBanner />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      forcedTheme="light"
      enableSystem={false}
      disableTransitionOnChange={false}
    >
      <TooltipProvider>
        <AuthProvider>
          <LRDirectGateProvider>
            <GuideProvider>
              <ChatProvider>
                <div className="relative">
                  <Toaster />
                  <Sonner />
                  <Router>
                    <ProductProvider>
                      <ScrollToTop />
                      <AppRoutes />
                    </ProductProvider>
                  </Router>
                </div>
              </ChatProvider>
            </GuideProvider>
          </LRDirectGateProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
